import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { checkoutAPI } from '../services/api';

function Checkout() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    fetchSession();
  }, [token]);

  const fetchSession = async () => {
    try {
      setLoading(true);
      const response = await checkoutAPI.getSession(token);
      setSession(response.data);
    } catch (err) {
      if (err.response?.status === 400) {
        setError(err.response.data.detail || 'Session expired or invalid');
      } else {
        setError('Failed to load checkout session');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const response = await checkoutAPI.complete(token, {
        customer_info: formData
      });
      
      setConfirmationCode(response.data.confirmation_code);
      setSuccess(true);
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to complete purchase. Please try again.');
      }
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div className="loading">Loading checkout...</div>;
  
  if (success) {
    return (
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Purchase Confirmed! 🎉</h1>
        </div>
        <div className="success-message">
          <h2>Confirmation Code: {confirmationCode}</h2>
          <p>Your tickets have been successfully purchased.</p>
          <p>A confirmation email will be sent to {formData.email}</p>
        </div>
        <button 
          className="btn btn-primary" 
          style={{ width: '100%', marginTop: '1rem' }}
          onClick={() => navigate('/')}
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  if (!session && !error) {
    return <div className="error-message">Invalid checkout session</div>;
  }

  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <h1>Complete Your Purchase</h1>
        {session && (
          <p style={{ color: '#666', marginTop: '0.5rem' }}>
            Session expires: {formatDate(session.expires_at)}
          </p>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {session && (
        <>
          <div className="order-summary">
            <h3>Order Summary</h3>
            <div className="summary-item">
              <span>Event:</span>
              <span>{session.event_name}</span>
            </div>
            <div className="summary-item">
              <span>Ticket Type:</span>
              <span className={`ticket-badge ${session.ticket_type.toLowerCase()}`}>
                {session.ticket_type}
              </span>
            </div>
            <div className="summary-item">
              <span>Quantity:</span>
              <span>{session.quantity}</span>
            </div>
            <div className="summary-item">
              <span>Price per Ticket:</span>
              <span>{formatCurrency(session.price_per_ticket)}</span>
            </div>
            <div className="summary-item total-row">
              <span>Total Amount:</span>
              <span>{formatCurrency(session.total_amount)}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <h3>Customer Information</h3>
            
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input
                type="text"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={submitting}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email Address *</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={submitting}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <input
                type="tel"
                name="phone"
                className="form-input"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
                required
                disabled={submitting}
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-success"
              style={{ width: '100%', marginTop: '1rem' }}
              disabled={submitting}
            >
              {submitting ? 'Processing...' : `Complete Purchase - ${formatCurrency(session.total_amount)}`}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

export default Checkout;